import React, { useEffect, useState } from 'react';
import { ProgressBar, Pagination } from 'react-bootstrap';
import './Traders.css';
import 'bootstrap/dist/css/bootstrap.min.css';
import AddressDisplay from '../addressdisplay/AddressDisplay';
import { FaSquareFull } from "react-icons/fa";
const Square = FaSquareFull as unknown as React.FC<React.SVGProps<SVGSVGElement>>;

interface PriceBoxProps {
    ca: string;
  chain: string;
}

interface TradersResult {
    address: string;
    avg_buy_price_usd: string;
    avg_sell_price_usd: string;
    total_tokens_bought: string;
    total_usd_invested: string;
    total_tokens_sold: string;
    total_sold_usd: string;
    avg_cost_of_quantity_sold: string;
    realized_profit_usd: string;
    count_of_trades: number;
    realized_profit_percentage: number;
}

interface TraderData {
  name: string;
  symbol: string;
  result: TradersResult[];
}

const Traders: React.FC<PriceBoxProps> = ({ ca, chain }) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [traderData, setTraderData] = useState<TraderData | null>(null);

  const [currentPage, setCurrentPage] = useState<number>(1);
  const itemsPerPage = 6;

  useEffect(() => {
    const fetchData = async () => {
      if (chain !== 'eth') {
        return;
      }
      
      setLoading(true);
      setError('');
      try {
        const response = await fetch(
          `/.netlify/functions/fetch-traders?ca=${ca}&chain=${chain}`
        );
        if (!response.ok) {
          throw new Error('Failed to fetch data');
        }
        const data = await response.json();
  
        data.result.sort(
          (a: TradersResult, b: TradersResult) =>
            parseFloat(b.realized_profit_usd) - parseFloat(a.realized_profit_usd)
        );
  
        setTraderData(data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
  
    if (ca && chain) {
      fetchData();
    }
  }, [ca, chain]);
  

  const formatUSD = (value: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  const formatVolume = (volume: number): string => {
    if (volume === null || volume === undefined) return '-';

    if (Math.abs(volume) >= 1_000_000_000) {
      return (
        (volume / 1_000_000_000).toLocaleString('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }) + 'B'
      );
    } else if (Math.abs(volume) >= 1_000_000) {
      return (
        (volume / 1_000_000).toLocaleString('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }) + 'M'
      );
    } else if (Math.abs(volume) >= 1_000) {
      return (
        (volume / 1_000).toLocaleString('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }) + 'K'
      );
    }
    return volume.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  // Handle pagination clicks
  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  if (error) {
    return <div className='not-found' style={{ color: 'red' }}>Error: {error}</div>;
  }

  if (!traderData || !traderData.result || traderData.result.length === 0) {
    return <div className='not-found'>No trading data available yet... 😥</div>;
  }

  // Pagination logic
  const totalSnipers = traderData.result.length;
  const totalPages = Math.ceil(totalSnipers / itemsPerPage);

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentSnipers = traderData.result.slice(indexOfFirstItem, indexOfLastItem);

  return (
    <>
      <div className="sniper-outer">
        {currentSnipers
        .map((item) => (
          <div className="sniper-box" key={item.address}>
            <div className="sniper-items">
              <div className="sniper-item">Trader</div>
              <div className="sniper-value">
                <AddressDisplay address={item.address} />
              </div>
            </div>

            <div className="sniper-items">
              <div className="sniper-item">Bought</div>
              <div
                className="sniper-value"
                style={{color: 'rgb(14, 203, 129)'}}
              >
                {formatUSD(Number(item.total_usd_invested))} 
              </div>
            </div>

            <div className="sniper-items">
              <div className="sniper-item">Sold</div>
              <div
                className="sniper-value"
                style={{color: 'rgb(246, 70, 93)'}}
              >
                {formatUSD(Number(item.total_sold_usd))} 
              </div>
            </div>

            <div className="sniper-items">
              <div className="sniper-item">Profit</div>
              <div
                className="sniper-value"
                style={{color: 'rgb(14, 203, 129)'}}
              >
                +{formatUSD(Number(item.realized_profit_usd))} 
              </div>
            </div>

            <div className="sniper-items">
              <div className="sniper-item">Profit</div>
              <div
                className="sniper-value"
              >
                {item.realized_profit_percentage.toFixed(2)}%
              </div>
            </div>

            <div className="sniper-items">
              <div className="sniper-item">Trades</div>
              <div
                className="sniper-value"
              >
                {(item.count_of_trades)} 
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Pagination Controls */}
      <div style={{ marginTop: '10px', display: 'flex', justifyContent: 'center' }}>
        <Pagination>
          <Pagination.Prev
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
          />
          {Array.from({ length: totalPages }, (_, index) => index + 1).map((page) => (
            <Pagination.Item
              key={page}
              active={page === currentPage}
              onClick={() => handlePageChange(page)}
            >
              {page}
            </Pagination.Item>
          ))}
          <Pagination.Next
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
          />
        </Pagination>
      </div>
    </>
  );
};

export default Traders;
